import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  PlayCircle, 
  CheckCircle, 
  Clock, 
  Mail,
  FileText,
  X,
  MessageSquare,
  Activity,
  Heart,
  BarChart3,
  Calendar,
  Phone,
  UserCheck,
  Eye,
  Plus,
  AlertTriangle,
  TrendingUp,
  Monitor,
  Stethoscope,
  ChevronRight,
  Send
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../services/contexts/AuthContext';
import { tenantNurseAPI } from '../../../services/api/tenantNurseAPI';
import { tenantNurseRecordingAPI } from '../../../services/api/tenantNurseRecordingAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import AuthenticatedMediaPlayer from '../../../components/AuthenticatedMediaPlayer';

const PatientRecordingManagement = () => {
  const { user, token } = useAuth();
  
  // Debug authentication
  console.log('🔍 Nurse interface auth state:', {
    user: !!user,
    token: !!token,
    userRole: user?.role,
    localStorage_token: !!localStorage.getItem('token'),
    localStorage_user: !!localStorage.getItem('user')
  });
  
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientRecordings, setPatientRecordings] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [patientSummary, setPatientSummary] = useState(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showSendLinkModal, setShowSendLinkModal] = useState(false);
  const [recordingChannel, setRecordingChannel] = useState('email');
  const [completedToday, setCompletedToday] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);

  useEffect(() => {
    // Only load patients if user is authenticated
    if (user && token) {
      loadPatients();
      loadDashboardStats();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const loadDashboardStats = async () => {
    try {
      // Get today's date
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      
      // This is a placeholder - in a real app, you'd have specific endpoints for stats
      // For now, we'll use dummy data that can be easily updated
      setCompletedToday(3);
      setPendingReviews(7);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients]);

  // Add Esc key handlers for all modals
  useEscKeyHandler(showSendLinkModal, () => setShowSendLinkModal(false));
  useEscKeyHandler(showRecordingModal, () => setShowRecordingModal(false));  
  useEscKeyHandler(showPatientModal, () => setShowPatientModal(false));

  const loadPatients = async () => {
    try {
      if (!user || !token) {
        console.log('No user or token available, skipping patient load');
        return;
      }
      
      setLoading(true);
      const response = await tenantNurseAPI.getPatients();
      setPatients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load patients:', error);
      // Only show error toast if it's not an authentication error
      if (error.response?.status !== 401) {
        toast.error('Failed to load patients');
      }
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const filterPatients = () => {
    if (!Array.isArray(patients)) {
      setFilteredPatients([]);
      return;
    }
    
    if (!searchTerm) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  };

  const handleViewPatientRecordings = async (patient) => {
    try {
      if (!user || !token) {
        toast.error('Authentication required');
        return;
      }
      
      setSelectedPatient(patient);
      setLoading(true);
      
      // Load patient recordings and care summary
      const [recordingsResponse, summaryResponse] = await Promise.all([
        tenantNurseRecordingAPI.getPatientRecordings(patient.id),
        tenantNurseRecordingAPI.getPatientCareSummary(patient.id)
      ]);
      
      setPatientRecordings(recordingsResponse.data.sessions || []);
      setPatientSummary(summaryResponse.data);
      setShowPatientModal(true);
    } catch (error) {
      console.error('Failed to load patient recordings:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load patient recordings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewRecordingDetails = async (recording) => {
    try {
      if (!user || !token) {
        toast.error('Authentication required');
        return;
      }
      
      setLoading(true);
      const response = await tenantNurseRecordingAPI.getRecordingDetails(recording.session_id);
      setSelectedRecording(response.data);
      setShowRecordingModal(true);
    } catch (error) {
      console.error('Failed to load recording details:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load recording details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendRecordingLink = async () => {
    try {
      if (!selectedPatient || !user || !token) {
        toast.error('Authentication required');
        return;
      }
      
      await tenantNurseAPI.sendRecordingLink(selectedPatient.id, recordingChannel);
      toast.success(`Recording link sent via ${recordingChannel}`);
      setShowSendLinkModal(false);
      
      // Refresh patient data
      handleViewPatientRecordings(selectedPatient);
    } catch (error) {
      console.error('Failed to send recording link:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to send recording link');
      }
    }
  };

  const PatientModal = () => {
    if (!selectedPatient || !patientSummary) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="h-6 w-6 text-purple-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedPatient.alias}</h2>
                  <p className="text-sm text-gray-500">{selectedPatient.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowSendLinkModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Send className="h-4 w-4" />
                  <span>Send Recording Link</span>
                </button>
                <button
                  onClick={() => setShowPatientModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Care Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Monitor className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm text-purple-600">Total Sessions</p>
                    <p className="text-2xl font-bold text-purple-900">{patientSummary.care_summary?.total_recording_sessions || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-green-600">Completed</p>
                    <p className="text-2xl font-bold text-green-900">{patientSummary.care_summary?.completed_sessions || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-8 w-8 text-orange-600 mr-3" />
                  <div>
                    <p className="text-sm text-orange-600">Pending Review</p>
                    <p className="text-2xl font-bold text-orange-900">{patientSummary.care_summary?.pending_doctor_review || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm text-blue-600">This Month</p>
                    <p className="text-2xl font-bold text-blue-900">{patientSummary.care_summary?.sessions_last_30_days || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Care Coordination Status */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Stethoscope className="h-5 w-5 text-blue-600 mr-2" />
                Care Coordination
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Last Request:</span>
                  <span className="ml-2 font-medium">
                    {patientSummary.care_coordination?.last_recording_request 
                      ? new Date(patientSummary.care_coordination.last_recording_request).toLocaleDateString()
                      : 'None'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Follow-up Needed:</span>
                  <span className={`ml-2 font-medium ${patientSummary.care_coordination?.follow_up_needed ? 'text-orange-600' : 'text-green-600'}`}>
                    {patientSummary.care_coordination?.follow_up_needed ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Can Send Request:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {patientSummary.care_coordination?.can_send_new_request ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Latest Vitals */}
            {patientSummary.latest_vitals && (
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Heart className="h-5 w-5 text-red-600 mr-2" />
                  Latest Vitals
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2 font-medium">
                      {new Date(patientSummary.latest_vitals.session_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Heart Rate:</span>
                    <span className="ml-2 font-medium">{patientSummary.latest_vitals.heart_rate || 'N/A'} bpm</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Blood Pressure:</span>
                    <span className="ml-2 font-medium">{patientSummary.latest_vitals.blood_pressure || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recording Sessions */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <PlayCircle className="h-5 w-5 text-gray-600 mr-2" />
                Recording Sessions
              </h3>
              <div className="space-y-3">
                {Array.isArray(patientRecordings) && patientRecordings.length > 0 ? (
                  patientRecordings.map((recording) => (
                    <div key={recording.session_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {new Date(recording.created_at).toLocaleDateString()} at {new Date(recording.created_at).toLocaleTimeString()}
                            </span>
                            {recording.doctor_reviewed && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                            {patientSummary.care_coordination?.follow_up_needed && !recording.doctor_reviewed && (
                              <AlertTriangle className="h-4 w-4 text-orange-500" />
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Questions:</span>
                              <span className="ml-2 font-medium">{recording.completed_questions}/{recording.total_questions}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Completion:</span>
                              <span className="ml-2 font-medium">{recording.completion_rate}%</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Status:</span>
                              <span className="ml-2 font-medium">{recording.status}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Method:</span>
                              <span className="ml-2 font-medium">{recording.recording_method || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleViewRecordingDetails(recording)}
                          className="ml-4 flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View Details</span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Monitor className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Recording Sessions</h3>
                    <p className="text-gray-500">Send a recording link to get started.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RecordingModal = () => {
    if (!selectedRecording) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Recording Session Details</h2>
                <p className="text-sm text-gray-500">Session ID: {selectedRecording.session_id}</p>
              </div>
              <button
                onClick={() => setShowRecordingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Session Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Session Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Date:</span>
                  <span className="ml-2 font-medium">
                    {new Date(selectedRecording.session_info.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <span className="ml-2 font-medium">{selectedRecording.session_info.status}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Duration:</span>
                  <span className="ml-2 font-medium">{Math.round(selectedRecording.session_info.total_duration)}s</span>
                </div>
                <div>
                  <span className="text-gray-600">Files:</span>
                  <span className="ml-2 font-medium">{selectedRecording.session_info.total_files}</span>
                </div>
              </div>
            </div>

            {/* Vitals */}
            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Vital Signs</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Heart Rate:</span>
                  <span className="ml-2 font-medium">{selectedRecording.vitals.heart_rate || 'N/A'} bpm</span>
                </div>
                <div>
                  <span className="text-gray-600">Blood Pressure:</span>
                  <span className="ml-2 font-medium">
                    {selectedRecording.vitals.blood_pressure_systolic && selectedRecording.vitals.blood_pressure_diastolic
                      ? `${selectedRecording.vitals.blood_pressure_systolic}/${selectedRecording.vitals.blood_pressure_diastolic}`
                      : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Quality:</span>
                  <span className={`ml-2 font-medium ${selectedRecording.vitals.quality_flag === 'good' ? 'text-green-600' : 'text-orange-600'}`}>
                    {selectedRecording.vitals.quality_flag || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Questions and Responses */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Questions and Responses</h3>
              <div className="space-y-6">
                {selectedRecording?.questions_and_responses?.map((questionResponse, index) => (
                  <div key={questionResponse.response_id} className="bg-white p-4 rounded-lg border">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Question {questionResponse.question_order}
                        </h4>
                        <p className="text-gray-700 mb-3">{questionResponse.question_text}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {questionResponse.question_type.replace('_', ' ').toUpperCase()}
                          </span>
                          <span>Duration: {questionResponse.recording_duration}s</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Response Details */}
                    {questionResponse.status !== 'pending' && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Patient Response
                          </span>
                          <span className="text-xs text-gray-500">
                            {questionResponse.completed_at ? new Date(questionResponse.completed_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        
                        {/* Media Files */}
                        {questionResponse.media_files && questionResponse.media_files.length > 0 && (
                          <div className="space-y-3">
                            {questionResponse.media_files.map((media, mediaIndex) => (
                              <div key={media.file_id} className="border rounded-lg p-3 bg-white">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 text-xs rounded ${
                                      media.file_type === 'audio' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      {media.file_type.toUpperCase()}
                                    </span>
                                    <span className="text-sm text-gray-600">
                                      {media.duration}s
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      ({(media.file_size / 1024).toFixed(1)} KB)
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Authenticated Media Player */}
                                <AuthenticatedMediaPlayer 
                                  mediaId={media.file_id}
                                  mediaType={media.file_type}
                                  className="mt-2"
                                  onError={(error) => {
                                    console.error('Media player error:', error);
                                    toast.error(`Failed to load ${media.file_type}: ${error.message}`);
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* No Media Files */}
                        {(!questionResponse.media_files || questionResponse.media_files.length === 0) && (
                          <div className="text-sm text-gray-500 italic">
                            No media files recorded for this question
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* No Response */}
                    {questionResponse.status === 'pending' && (
                      <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                        <div className="text-sm text-yellow-800">
                          No response recorded for this question
                        </div>
                      </div>
                    )}
                  </div>
                )) || (
                  <div className="text-center py-8 text-gray-500">
                    No questions found for this recording
                  </div>
                )}
              </div>
            </div>

            {/* Care Notes */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Care Notes & Clinical Review</h3>
              
              {selectedRecording.care_notes ? (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-800">Clinical Notes Available</span>
                    {selectedRecording.doctor_reviewed && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-gray-800">
                    {selectedRecording.care_notes}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No care notes or clinical review available yet</p>
                  {!selectedRecording.doctor_reviewed && (
                    <p className="text-sm text-orange-600 mt-1">Awaiting doctor review</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SendLinkModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-md w-full">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Send Recording Link</h2>
            <p className="text-sm text-gray-500 mt-1">
              Send to: {selectedPatient?.alias} ({selectedPatient?.email})
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Method
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="email"
                      checked={recordingChannel === 'email'}
                      onChange={(e) => setRecordingChannel(e.target.value)}
                      className="mr-2"
                    />
                    <Mail className="h-4 w-4 text-gray-500 mr-2" />
                    <span>Email</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="sms"
                      checked={recordingChannel === 'sms'}
                      onChange={(e) => setRecordingChannel(e.target.value)}
                      className="mr-2"
                    />
                    <Phone className="h-4 w-4 text-gray-500 mr-2" />
                    <span>SMS</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={() => setShowSendLinkModal(false)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSendRecordingLink}
              className="flex items-center space-x-2 px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              <Send className="h-4 w-4" />
              <span>Send Link</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!user || !token) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-gray-500 mb-4">
            <Users className="h-12 w-12 mx-auto mb-4" />
            <p>Please log in to access patient recording management</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !showPatientModal) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Recording Management</h1>
          <p className="text-gray-600">Manage patient recordings and coordinate care</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <dt className="text-sm font-medium text-gray-500">Total Patients</dt>
              <dd className="text-2xl font-semibold text-gray-900">{filteredPatients.length}</dd>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PlayCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <dt className="text-sm font-medium text-gray-500">Active Patients</dt>
              <dd className="text-2xl font-semibold text-gray-900">
                {filteredPatients.filter(p => p.status === 'active').length}
              </dd>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <dt className="text-sm font-medium text-gray-500">Completed Today</dt>
              <dd className="text-2xl font-semibold text-gray-900">
                {/* This will be populated with real data */}
                {completedToday}
              </dd>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <dt className="text-sm font-medium text-gray-500">Pending Reviews</dt>
              <dd className="text-2xl font-semibold text-gray-900">
                {/* This will be populated with real data */}
                {pendingReviews}
              </dd>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Patients List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(filteredPatients) && filteredPatients.length > 0 ? (
                filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Users className="h-8 w-8 text-purple-500 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{patient.alias}</div>
                          <div className="text-sm text-gray-500">ID: {patient.id?.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{patient.email}</div>
                      <div className="text-sm text-gray-500">{patient.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {patient.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewPatientRecordings(patient)}
                        className="flex items-center space-x-2 text-purple-600 hover:text-purple-900"
                      >
                        <Monitor className="h-4 w-4" />
                        <span>Manage Recordings</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    {loading ? 'Loading patients...' : 'No patients found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!Array.isArray(filteredPatients) || filteredPatients.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria' : 'No patients available for care management'}
          </p>
        </div>
      ) : null}

      {/* Modals */}
      {showPatientModal && <PatientModal />}
      {showRecordingModal && <RecordingModal />}
      {showSendLinkModal && <SendLinkModal />}
    </div>
  );
};

export default PatientRecordingManagement;